import React, {useState, useEffect, useContext} from "react";
import ModuleSearch from "./moduleSearch";
import CourseCurriculumNewModuleForm from "./courseCurriculumNewModuleForm";
import {corePaths, IFlatList, ILessonEntity, IPaginatedResponse, IQuizEntity} from "@buildwitt/shapeshift-types";
import {makeStyles} from "tss-react/mui";
import {Box, Button, Typography} from "@mui/material";
import {Modal} from "@buildwitt/shapeshift-common/v2/modal";
import {APIState, INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import { ICurriculumItemImport, ICurriculumItemImportSlim } from "@/types/curriculum";
import { cohortBaseUrl, palette } from "@/constants";

export interface IProps {
	currentModules?: IFlatList[],
	typeFilter?: string,
	selectLimit?: number,
	mode: "new" | "import",
	lessonId?: number,
	onClose: (modules?: ICurriculumItemImport[]) => void,
	onDeleteResponse?: (response: APIState<ILessonEntity>) => void,
}

const useStyles = makeStyles()(
	(_theme) => ({
		newModuleModal: {
			zIndex: 50,
			"[class*='contentContainer']": {
				padding: "20px",
				maxHeight: "calc(100vh - 200px)",
				width: "1000px",

			},
		},
		tabs: {
			height: "70px",
			minHeight: "70px",
			borderBottom: palette.grey10 + " 1px solid",
			width: "100%",
			"& button": {
				width: "calc(50% - 8px)"
			},
		},
		container: {
			display: "flex",
			gap: "40px",
			flexDirection: "column"
		},
		body: {},
		selectedCountFooter: {
			color: "grey",
			paddingRight: "15px",
			fontSize: "12px"
		},
		header: {
		},
		subtitle: {
			color: "#F9F9F9B2"
		},
		title: {
			flexGrow: 2
		},
		fullScreen: {
			paddingRight: "20px",
			cursor: "pointer",
			"& span":{
				marginLeft: "10px"
			}
		}
	})
);

interface IFilterMap {
	[type: string]: Set<number>
}

const NewModuleModal: React.FunctionComponent<IProps> = (props) => {
	const {classes} = useStyles();
	const [selected, setSelected] = useState<ICurriculumItemImportSlim[]>([]);
	const [filterMap, setFilterMap] = useState<IFilterMap>();
	const [lessons, getLessons] = useApi<IPaginatedResponse<ILessonEntity[]>>();
	const [quizzes, getQuizzes] = useApi<IPaginatedResponse<IQuizEntity[]>>();
	const [requestedClose, setRequestedClose] = useState(false);
	const selectedLessons = selected?.filter(e => e.type === corePaths.lesson).map(e => e.id);
	const selectedQuizzes = selected?.filter(e => e.type === corePaths.quiz).map(e => e.id);

	const handleNewModuleSubmission = (module: ICurriculumItemImport): void => {
		props.onClose([module]);
	}

	//Create filter map for rapid lookup
	useEffect(() => {
		if (!props.currentModules)
			return;

		const map: IFilterMap = {};
		props.currentModules.forEach(e => {
			if (!map[e.type]) map[e.type] = new Set<number>();
			map[e.type].add(e.moduleId);
		});

		setFilterMap(map)
	}, [props.currentModules])

	const handleClose = () => {
		getLessons({
			url: cohortBaseUrl + "/lesson", 
			data: {
				page: 1, 
				pageSize: 100, 
				extendedAccess: true,
				filters: JSON.stringify({
					concatinator: "and", 
					conditions: [{attribute: "id", operator: "in", value: selectedLessons}]
				})
			}
		});
		getQuizzes({
			url: cohortBaseUrl + "/quiz", 
			data: {
				page: 1, 
				pageSize: 100, 
				extendedAccess: true,
				filters: JSON.stringify({
					concatinator: "and", 
					conditions: [{attribute: "id", operator: "in", value: selectedQuizzes}]
				})
			}
		});
		setRequestedClose(true);
	}

	useEffect(() => {
		if (!requestedClose) 
			return;
		if (
			(selectedLessons.length === 0 || (lessons?.data && !lessons.isLoading))
			&& (selectedQuizzes.length === 0 || (quizzes?.data && !quizzes.isLoading))
		) {
			const newSelectedLessons = lessons.data?.data.map(e => ({
				id: e.id,
				type: corePaths.lesson,
				title: e.title,
				slug: e.slug,
				publishedAt: e.publishedAt ? new Date(e.publishedAt) : undefined,
			}));
			const newSelectedQuizzes = quizzes.data?.data.map(e => ({
				id: e.id,
				type: corePaths.quiz,
				title: e.title,
				slug: e.slug,
				publishedAt: e.publishedAt ? new Date(e.publishedAt) : undefined,
			}));
			
			const newSelected = [...(newSelectedLessons ?? []), ...(newSelectedQuizzes ?? [])];
			props.onClose(newSelected);
		}
	}, [lessons, quizzes, requestedClose]);

	return (
		<Modal 
			open={true} 
			title="Confirm Bulk Completion"
			className={classes.newModuleModal} 
			onClose={() => props.onClose()} 
			footerButtons={props.mode === "import" ? [
				<Button key="cancel" onClick={() => props.onClose()}>
					Cancel
				</Button>,
				selected.length > 0 ? <Typography key="stat" className={classes.selectedCountFooter}>Selected {selected.length} lessons</Typography> : null,
				<Button key="save" disabled={selected.length === 0} onClick={() => handleClose()}>Add</Button>
			] : undefined}
		>
			<Box className={classes.container}>
				<Box className={classes.header}>
					<Typography className={classes.title} variant="h6" id="title">
						{props.mode === "import" ? "Choose Lessons" : props.lessonId ? "Edit Lesson" : "Create New Lesson"}
					</Typography>
				</Box>
					{props.mode === "import" && 
						<Typography className={classes.subtitle}>
							Select up to 20 lessons. You&apos;ll be able to arrange and organize your lessons after adding them to your course. 
						</Typography>
					}

					{props.mode === "import" && <ModuleSearch
						selected={selected}
						typeFilter={props.typeFilter}
						filterMap={filterMap}
						onChange={e => setSelected(e)}
					/>}

					{props.mode === "new" && <CourseCurriculumNewModuleForm
						typeFilter={props.typeFilter}
						lessonId={props.lessonId}
						onSubmit={e => handleNewModuleSubmission(e)}
						onCancel={() => props.onClose()}
						onDeleteResponse={props.onDeleteResponse}
					/>}
			</Box>
		</Modal>
	);
};

export default NewModuleModal;
